import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import apiService from "../services/api";
import BrandLogo from "./BrandLogo";

function MobileIcon({ name }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
  };

  const icons = {
    home: (
      <>
        <path d="M3 10.5L12 3l9 7.5" />
        <path d="M5.5 9.5V20h13V9.5" />
      </>
    ),
    pantry: (
      <>
        <rect x="5" y="4" width="14" height="16" rx="2.5" />
        <path d="M9 4V2.5h6V4" />
        <path d="M9 9h6" />
      </>
    ),
    lists: (
      <>
        <path d="M9 6.5h10" />
        <path d="M9 12h10" />
        <path d="M9 17.5h10" />
        <path d="M4.5 6.5h.01" />
        <path d="M4.5 12h.01" />
        <path d="M4.5 17.5h.01" />
      </>
    ),
    meals: (
      <>
        <rect x="4" y="5" width="16" height="15" rx="2.5" />
        <path d="M8 3v4" />
        <path d="M16 3v4" />
        <path d="M4 10h16" />
      </>
    ),
    recipes: (
      <>
        <rect x="4" y="3.5" width="16" height="17" rx="2.5" />
        <path d="M8 7.5h8" />
        <path d="M8 11.5h8" />
        <path d="M8 15.5h5" />
      </>
    ),
    feed: (
      <>
        <circle cx="12" cy="8" r="3" />
        <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
      </>
    ),
    ai: (
      <>
        <path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4z" />
        <path d="M18 15l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
      </>
    ),
    trophy: (
      <>
        <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
        <path d="M9 20h6" />
        <path d="M12 15v5" />
        <path d="M16 6h2a2 2 0 0 1-2 3" />
        <path d="M8 6H6a2 2 0 0 0 2 3" />
      </>
    ),
    chart: (
      <>
        <path d="M5 19V9" />
        <path d="M12 19V5" />
        <path d="M19 19v-7" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19 12a7 7 0 0 0-.08-1l2.03-1.58-2-3.46-2.46.8a7 7 0 0 0-1.74-1L14.5 2h-5l-.25 2.76a7 7 0 0 0-1.74 1l-2.46-.8-2 3.46L5.08 11a7 7 0 0 0 0 2l-2.03 1.58 2 3.46 2.46-.8a7 7 0 0 0 1.74 1L9.5 22h5l.25-2.76a7 7 0 0 0 1.74-1l2.46.8 2-3.46L18.92 13c.05-.33.08-.66.08-1z" />
      </>
    )
  };

  return <svg {...common}>{icons[name] || icons.home}</svg>;
}

const navItems = [
  { to: "/dashboard", label: "Home", icon: "home", shortLabel: "Home" },
  { to: "/pantry", label: "Pantry", icon: "pantry", shortLabel: "Pantry" },
  { to: "/shopping", label: "Lists", icon: "lists", shortLabel: "Lists" },
  { to: "/recipes", label: "Recipes", icon: "recipes", shortLabel: "Recipes" },
  { to: "/assistant", label: "AI", icon: "ai", shortLabel: "AI" }
];

const drawerItems = [
  { to: "/dashboard", label: "Dashboard", icon: "home" },
  { to: "/pantry", label: "My Pantry", icon: "pantry" },
  { to: "/shopping", label: "Shopping Lists", icon: "lists" },
  { to: "/meal-plans", label: "Meal Plans", icon: "meals" },
  { to: "/recipes", label: "Recipes", icon: "recipes" },
];

const secondaryDrawerItems = [
  { to: "/feed", label: "Cooking Feed", icon: "feed" },
  { to: "/assistant", label: "Sous-chef AI", icon: "ai" },
  { to: "/achievements", label: "Achievements", icon: "trophy" },
  { to: "/leaderboard", label: "Leaderboard", icon: "chart" },
];

const accountDrawerItems = [
  { to: "/profile", label: "Profile", icon: "profile" },
  { to: "/settings", label: "Settings", icon: "settings" }
];

const routeTitles = {
  "/": { eyebrow: "Overview", title: "Kitchen Control" },
  "/dashboard": { eyebrow: "Overview", title: "Kitchen Control" },
  "/pantry": { eyebrow: "Inventory", title: "Pantry Flow" },
  "/shopping": { eyebrow: "Planning", title: "Shopping Lists" },
  "/meal-plans": { eyebrow: "Calendar", title: "Meal Plan Studio" },
  "/recipes": { eyebrow: "Discovery", title: "Recipe Library" },
  "/assistant": { eyebrow: "Guidance", title: "Sous-chef AI" },
  "/feed": { eyebrow: "Community", title: "Cooking Feed" },
  "/achievements": { eyebrow: "Progress", title: "Achievements" },
  "/leaderboard": { eyebrow: "Stats", title: "Leaderboard" },
  "/profile": { eyebrow: "Account", title: "Profile" },
  "/settings": { eyebrow: "Preferences", title: "Settings" },
  "/spin-wheel": { eyebrow: "Bonus", title: "Spin the Wheel" },
  "/admin": { eyebrow: "Admin", title: "Control Room" }
};

const matchRouteMeta = (pathname) => {
  if (pathname.startsWith("/recipes/")) {
    return { eyebrow: "Recipe", title: "Recipe Detail" };
  }

  return routeTitles[pathname] || { eyebrow: "PrepRight", title: "Cook Smarter" };
};

export default function MobileChrome({ isOpen, onToggleMenu, onCloseMenu }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { notifications, unreadCount, markAllAsRead, clearAllNotifications, loadNotifications, removeNotification } = useNotifications();
  const meta = matchRouteMeta(location.pathname);
  const [isExpanded, setIsExpanded] = useState(true);
  const [stats, setStats] = useState({ streak: 0, hasCompletedToday: false });
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const handleFriendRequestAction = async (notification, action) => {
    const requestId = notification.data?.requestId;
    if (!requestId) return;

    try {
      if (action === "accept") {
        await apiService.acceptFriendRequest(requestId, notification.id);
      } else {
        await apiService.declineFriendRequest(requestId, notification.id);
      }
      removeNotification(notification.id);
      await loadNotifications();
      window.dispatchEvent(new CustomEvent("friendsRefresh"));
    } catch (error) {
      console.error(`Failed to ${action} friend request:`, error);
    }
  };

  useEffect(() => {
    setIsExpanded(true);
  }, [location.pathname]);

  useEffect(() => {
    const loadStats = async () => {
      if (!user || !apiService.getToken()) return;

      try {
        const response = await apiService.getActivityStats();
        setStats({
          streak: response?.stats?.streak || 0,
          hasCompletedToday: Boolean(response?.stats?.hasCompletedToday)
        });
      } catch (error) {
        console.error("Failed to load mobile chrome stats:", error);
      }
    };

    loadStats();
  }, [user]);

  useEffect(() => {
    if (user && apiService.getToken()) {
      loadNotifications();
    }
  }, [user, loadNotifications]);

  useEffect(() => {
    const handleStatsRefresh = async () => {
      if (!user || !apiService.getToken()) return;

      try {
        const response = await apiService.getActivityStats();
        setStats({
          streak: response?.stats?.streak || 0,
          hasCompletedToday: Boolean(response?.stats?.hasCompletedToday)
        });
      } catch (error) {
        console.error("Failed to refresh mobile chrome stats:", error);
      }
    };

    window.addEventListener("statsRefresh", handleStatsRefresh);
    return () => window.removeEventListener("statsRefresh", handleStatsRefresh);
  }, [user]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const chrome = document.querySelector(".mobile-chrome");
      const drawer = document.querySelector(".mobile-drawer");
      const notificationsModal = document.querySelector(".mobile-chrome__notifications-modal");

      if (notificationsModal && notificationsModal.contains(event.target)) {
        return;
      }

      if (drawer && drawer.contains(event.target)) {
        return;
      }

      if (chrome && chrome.contains(event.target)) {
        setIsExpanded(true);
        return;
      }

       setIsExpanded(false);
       setNotificationsOpen(false);
     };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  return (
    <>
      <header className={`mobile-chrome ${isExpanded ? "" : "is-condensed"}`}>
        <div className="mobile-chrome__topline">
          <button
            type="button"
            className="mobile-chrome__menu"
            aria-label="Open navigation"
            onClick={onToggleMenu}
          >
            <span />
            <span />
            <span />
          </button>

          <button
            type="button"
            className="mobile-chrome__identity"
            onClick={() => navigate("/dashboard")}
          >
            <BrandLogo className="mobile-chrome__badge-logo" size="small" />
            <span className="mobile-chrome__identity-copy">
              <strong>PrepRight</strong>
              <small>Mobile beta</small>
            </span>
          </button>

          <div className="mobile-chrome__actions">
            <button
              type="button"
              className={`mobile-chrome__streak-pill ${stats.hasCompletedToday ? "is-active" : "is-idle"}`}
              onClick={() => navigate("/dashboard")}
              aria-label={`Current streak ${stats.streak || 0} days`}
            >
              <span className="mobile-chrome__streak-flame">🔥</span>
              <span>{stats.streak || 0}</span>
            </button>

            <button
              type="button"
              className="mobile-chrome__notify"
              aria-label="Open notifications"
              onClick={() => setNotificationsOpen((open) => !open)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mobile-chrome__notify-icon">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {unreadCount > 0 && <span className="mobile-chrome__notify-badge">{unreadCount}</span>}
            </button>

            <button
              type="button"
              className="mobile-chrome__profile"
              aria-label={user ? "Open profile" : "Log in"}
              onClick={() => navigate(user ? "/profile" : "/login")}
            >
              {user?.name?.charAt(0) || "U"}
            </button>
          </div>
        </div>

        <div className="mobile-chrome__hero">
          <div>
            <div className="mobile-chrome__eyebrow">{meta.eyebrow}</div>
            <h1 className="mobile-chrome__title">{meta.title}</h1>
          </div>
          <div className="mobile-chrome__meta">
            <span className="mobile-chip">{stats.hasCompletedToday ? "Streak active" : "Keep streak alive"}</span>
            {unreadCount > 0 && <span className="mobile-chip mobile-chip--alert">{unreadCount} new</span>}
          </div>
        </div>
      </header>

      {notificationsOpen && (
        <div className="notifications-modal-overlay mobile-chrome__notifications-overlay" onClick={() => setNotificationsOpen(false)}>
          <div className="notifications-modal mobile-chrome__notifications-modal" onClick={(event) => event.stopPropagation()}>
            <div className="notifications-header">
              <h2>Notifications</h2>
              <div className="notifications-header-actions">
                {notifications.length > 0 && unreadCount > 0 && (
                  <button className="mark-all-read-btn" onClick={markAllAsRead}>
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button className="clear-all-notifications-btn" onClick={clearAllNotifications}>
                    Clear all
                  </button>
                )}
                <button className="close-notifications-btn" onClick={() => setNotificationsOpen(false)}>
                  x
                </button>
              </div>
            </div>
            <div className="notifications-content">
              {notifications.length > 0 ? (
                <div className="regular-notifications-section">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="notification-item">
                      <div className="notification-icon">
                        {notification.type === "achievement" && "🏆"}
                        {notification.type === "level_up" && "⭐"}
                        {notification.type === "like" && "❤️"}
                        {notification.type === "comment" && "💬"}
                        {notification.type === "system" && "🔔"}
                        {notification.type === "friend_request" && "👥"}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">
                          {new Date(notification.timestamp || notification.createdAt).toLocaleDateString()}
                        </div>
                        {notification.type === "friend_request" && notification.data?.requestId && (
                          <div className="notification-friend-actions">
                            <button
                              type="button"
                              className="accept-friend-btn"
                              onClick={() => handleFriendRequestAction(notification, "accept")}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="decline-friend-btn"
                              onClick={() => handleFriendRequestAction(notification, "decline")}
                            >
                              Decline
                            </button>
                          </div>
                        )}
                      </div>
                      {!notification.isRead && <div className="unread-indicator" />}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-notifications">
                  <div className="no-notifications-icon">🔔</div>
                  <p>No notifications yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`mobile-drawer-backdrop ${isOpen ? "is-open" : ""}`} onClick={onCloseMenu} />
      <aside className={`mobile-drawer ${isOpen ? "is-open" : ""}`} aria-hidden={!isOpen}>
        <div className="mobile-drawer__header">
          <div>
            <div className="mobile-drawer__label">{user ? "Signed in as" : "Browsing as"}</div>
            <div className="mobile-drawer__name">{user?.name || "Guest"}</div>
          </div>
          <button type="button" className="mobile-drawer__close" onClick={onCloseMenu} aria-label="Close navigation">
            x
          </button>
        </div>

        <nav className="mobile-drawer__nav">
          <div className="mobile-drawer__section">
            <div className="mobile-drawer__section-title">Main</div>
            {drawerItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `mobile-drawer__link ${isActive ? "is-active" : ""}`}
                onClick={onCloseMenu}
              >
                <span className="mobile-drawer__icon">
                  <MobileIcon name={item.icon} />
                </span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="mobile-drawer__section">
            <div className="mobile-drawer__section-title">Community & Progress</div>
            {secondaryDrawerItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `mobile-drawer__link ${isActive ? "is-active" : ""}`}
                onClick={onCloseMenu}
              >
                <span className="mobile-drawer__icon">
                  <MobileIcon name={item.icon} />
                </span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="mobile-drawer__section mobile-drawer__section--account">
            <div className="mobile-drawer__section-title">Account</div>
            {accountDrawerItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `mobile-drawer__link ${isActive ? "is-active" : ""}`}
                onClick={onCloseMenu}
              >
                <span className="mobile-drawer__icon">
                  <MobileIcon name={item.icon} />
                </span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="mobile-drawer__footer">
          {user ? (
            <button
              type="button"
              className="mobile-drawer__danger"
              onClick={() => {
                logout();
                onCloseMenu();
                navigate("/login");
              }}
            >
              Log out
            </button>
          ) : (
            <div className="mobile-drawer__guest-actions">
              <button type="button" onClick={() => { onCloseMenu(); navigate("/login"); }}>
                Log in
              </button>
              <button type="button" onClick={() => { onCloseMenu(); navigate("/register"); }}>
                Register
              </button>
            </div>
          )}
        </div>
      </aside>

      <nav className="mobile-dock" aria-label="Primary mobile navigation">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `mobile-dock__item ${isActive ? "is-active" : ""}`}
          >
            <span className="mobile-dock__icon">
              <MobileIcon name={item.icon} />
            </span>
            <span className="mobile-dock__label">{item.shortLabel}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
